/**
 * Zod schemas for ActionTypeEntry and related registry types.
 *
 * Used to validate action type definitions from the server.
 *
 * The inferred types narrow to the domain unions declared in
 * `registries/core.ts` and `registries/data-types.ts` so that a
 * schema-parsed value drops directly into code typed as
 * `ActionTypeEntry` (from the registry) without an additional cast.
 * The admin create/update endpoints validate these unions on write,
 * so at read time they are safe.
 */

import { z } from 'zod';
import { DATA_TYPES } from './registries/data-types';

const ACTION_CATEGORIES = [
  'decision',
  'communication',
  'integration',
  'navigation',
  'lifecycle',
  'bundle_synthesis',
] as const;
const ACTION_SCOPES = ['item', 'bundle'] as const;

// InteractionPresetEntry

export const InteractionPresetEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  // Plain string, like `InteractionPresetEntry.interactionMode` in the registry type: stored
  // presets carry legacy modes (prod `approve` has 'modal') that are not registry ids.
  interactionMode: z.string(),
  inputFields: z.array(z.object({
    key: z.string(),
    type: z.enum(DATA_TYPES),
    label: z.string(),
    required: z.boolean().optional(),
    placeholder: z.string().optional(),
    prefillFrom: z.string().optional(),
    displayMode: z.enum(['input', 'review']).optional(),
  })).readonly().optional(),
  requiredDataFields: z.array(z.object({
    key: z.string(),
    type: z.enum(DATA_TYPES),
    purpose: z.string(),
  })).readonly().optional(),
});
export type InteractionPresetEntry = z.infer<typeof InteractionPresetEntrySchema>;

// ActionTypeEntry

export const ActionTypeEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  icon: z.string(),
  description: z.string(),
  category: z.enum(ACTION_CATEGORIES),
  // Plain string, not the registry-id enum: prod `action_types` holds enabled rows (snooze,
  // send_followup) with default_interaction = 'modal', which is not an interaction registry
  // id. The column is free text, and one such row failed the whole list parse. Consumers
  // resolve it through getInteractionEntry, which tolerates unknown ids.
  defaultInteraction: z.string(),
  isDecision: z.boolean(),
  isTerminal: z.boolean(),
  isRepeatable: z.boolean().optional(),
  executionLocation: z.enum(['frontend', 'backend']),
  defaultOutcome: z.object({ status: z.string() }).optional(),
  requiredDataFields: z.array(z.object({
    key: z.string(),
    type: z.enum(DATA_TYPES),
  })).readonly().optional(),
  interactionPresets: z.array(InteractionPresetEntrySchema).readonly().optional(),
  suggestedHandler: z.string().optional(),
  scope: z.enum(ACTION_SCOPES),
  supportsBulk: z.boolean(),
  isRecommended: z.boolean(),
});
export type ActionTypeEntry = z.infer<typeof ActionTypeEntrySchema>;
